/**
 * ADD CREATION MODAL
 * 
 * Create a new creation (mani, press-on set, jewelry, etc.)
 * - Upload photos
 * - Select items used from collection
 * - Auto-update usage counts on save:
 *   - New creation: increment all items
 *   - Edit creation: increment newly added items, decrement removed items
 * - Optional marketplace listing
 */

import React, { useState, useEffect } from 'react';
import { X, Upload, Plus, Trash2, Search, Check, Store, ChevronDown, ChevronRight, Image } from 'lucide-react';
import { collection, addDoc, doc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import { getUserItems } from '../../firebase/db';
import { uploadPhoto, deletePhoto } from '../../services/photoUploadService';
import StorageUpgradeModal from '../StorageUpgradeModal';
import type { Creation, ItemUsed, PressOnDetails } from '../../types/creationTypes';
import { itemsUsedRefs as computeItemsUsedRefs } from '../../utils/creationRefs';
import type { CollectionItem, Photo } from '../../types';
import { TECHNIQUES, NAIL_SHAPES, NAIL_LENGTHS, MARKETPLACE_CATEGORIES, CREATION_TYPES } from '../../types/creationTypes';
import { ACCEPTED_IMAGE_TYPES } from '../../utils/imageUtils';

interface AddCreationModalProps {
  onClose: () => void;
  onSave: () => void;
  editingCreation?: Creation;  // For edit mode
}

const AddCreationModal: React.FC<AddCreationModalProps> = ({ onClose, onSave, editingCreation }) => {
  const [saving, setSaving] = useState(false);
  
  // Required fields
  const [title, setTitle] = useState(editingCreation?.title || '');
  const [description, setDescription] = useState(editingCreation?.description || '');
  const [quantity, setQuantity] = useState(editingCreation?.quantity?.toString() || '1');
  const [photos, setPhotos] = useState<Photo[]>(editingCreation?.photos || []);
  const [itemsUsed, setItemsUsed] = useState<ItemUsed[]>(editingCreation?.itemsUsed || []);
  const [creationType, setCreationType] = useState(editingCreation?.creationType || 'mani');
  
  // Track original items for edit mode (to properly update usage counts)
  const [originalItemIds] = useState<Set<string>>(
    new Set(editingCreation?.itemsUsed?.map(item => item.itemId) || [])
  );
  
  // Optional fields
  const [dateCreated, setDateCreated] = useState(editingCreation?.dateCreated || '');
  const [techniques, setTechniques] = useState<string[]>(editingCreation?.techniques || []);
  const [difficulty, setDifficulty] = useState(editingCreation?.difficulty || '');
  const [timeToComplete, setTimeToComplete] = useState(editingCreation?.timeToComplete || '');
  
  // Marketplace fields
  const [listedOnMarketplace, setListedOnMarketplace] = useState(editingCreation?.listedOnMarketplace || false);
  const [marketplaceCategory, setMarketplaceCategory] = useState(editingCreation?.marketplaceCategory || '');
  const [price, setPrice] = useState(editingCreation?.price?.toString() || '');
  const [openToTrades, setOpenToTrades] = useState(editingCreation?.openToTrades || false);
  
  // Press-on specific
  const [shapesAvailable, setShapesAvailable] = useState<string[]>(editingCreation?.pressOnDetails?.shapesAvailable || []);
  const [lengthsAvailable, setLengthsAvailable] = useState<string[]>(editingCreation?.pressOnDetails?.lengthsAvailable || []);
  const [customSizing, setCustomSizing] = useState(editingCreation?.pressOnDetails?.customSizing || false);
  const [sizingKitAvailable, setSizingKitAvailable] = useState(editingCreation?.pressOnDetails?.sizingKitAvailable || false);
  const [guideUrl, setGuideUrl] = useState(editingCreation?.pressOnDetails?.guideUrl || '');
  
  // Item picker state
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [collectionItems, setCollectionItems] = useState<CollectionItem[]>([]);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [itemTypeFilter, setItemTypeFilter] = useState<string>('all');
  const [loadingItems, setLoadingItems] = useState(false);
  
  // Section toggles
  const [showOptional, setShowOptional] = useState(false);
  const [showMarketplace, setShowMarketplace] = useState(listedOnMarketplace);
  const [, setShowPressOnDetails] = useState(marketplaceCategory === 'pressons');

  // Load collection items for picker
  useEffect(() => {
    const loadItems = async () => {
      const user = auth.currentUser;
      if (!user) return;
      
      setLoadingItems(true);
      try {
        const items = await getUserItems(user.uid);
        setCollectionItems(items);
      } catch (error) {
        console.error('Error loading items:', error);
      } finally {
        setLoadingItems(false);
      }
    };
    
    if (showItemPicker) {
      loadItems();
    }
  }, [showItemPicker]);

  // Filter items for picker
  const filteredItems = collectionItems.filter(item => {
    const matchesSearch = !itemSearchQuery ||
      item.name?.toLowerCase().includes(itemSearchQuery.toLowerCase()) ||
      item.brand?.toLowerCase().includes(itemSearchQuery.toLowerCase());
    
    const matchesType = itemTypeFilter === 'all' || item.itemType === itemTypeFilter;
    
    // Don't show items already selected
    const notSelected = !itemsUsed.some(used => used.itemId === item.id);
    
    return matchesSearch && matchesType && notSelected;
  });

  // Storage upgrade modal state
  const [showStorageUpgradeModal, setShowStorageUpgradeModal] = useState(false);

  // Handle photo upload (with storage limit checking!)
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !auth.currentUser) return;

    for (const file of Array.from(files)) {
      try {
        const result = await uploadPhoto(
          auth.currentUser.uid,
          file,
          'creations',
          { isDefault: photos.length === 0 }
        );
        
        if (result.showUpgradeModal) {
          setShowStorageUpgradeModal(true);
          break;
        }
        
        if (result.success && result.photo) {
          setPhotos(prev => [...prev, { 
            ...result.photo!, 
            isDefault: prev.length === 0
          }]);
        }
      } catch (error) {
        console.error('Error uploading photo:', error);
      }
    }
    e.target.value = '';
  };

  // Set default photo
  const setDefaultPhoto = (index: number) => {
    setPhotos(prev => prev.map((photo, i) => ({
      ...photo,
      isDefault: i === index
    })));
  };

  // Remove photo
  const removePhoto = async (index: number) => {
    const photoToRemove = photos[index];
    const user = auth.currentUser;

    // Only delete from Storage if editing an existing creation
    // (new photos in add mode haven't been uploaded yet)
    if (editingCreation && user) {
      await deletePhoto(user.uid, photoToRemove.url);
    }

    setPhotos(prev => {
      const updated = prev.filter((_, i) => i !== index);
      // If we removed the default, make first one default
      if (updated.length > 0 && !updated.some(p => p.isDefault)) {
        updated[0].isDefault = true;
      }
      return updated;
    });
  };

  // Add item from collection
  const addItemUsed = (item: CollectionItem) => {
    const primaryPhoto = item.photos?.find(p => p.isDefault) || item.photos?.[0];
    
    const itemUsed: ItemUsed = {
      itemId: item.id,
      itemType: item.itemType as any,
      name: item.name,
      brand: item.brand || ''
    };
    
    // Only add photoUrl if it exists (Firestore doesn't accept undefined)
    if (primaryPhoto?.url) {
      itemUsed.photoUrl = primaryPhoto.url;
    }
    
    setItemsUsed(prev => [...prev, itemUsed]);
  };

  // Remove item used
  const removeItemUsed = (itemId: string) => {
    setItemsUsed(prev => prev.filter(item => item.itemId !== itemId));
  };

  // Toggle technique
  const toggleTechnique = (technique: string) => {
    setTechniques(prev => 
      prev.includes(technique)
        ? prev.filter(t => t !== technique)
        : [...prev, technique]
    );
  };

  // Toggle shape
  const toggleShape = (shape: string) => {
    setShapesAvailable(prev =>
      prev.includes(shape)
        ? prev.filter(s => s !== shape)
        : [...prev, shape]
    );
  };

  // Toggle length
  const toggleLength = (length: string) => {
    setLengthsAvailable(prev =>
      prev.includes(length)
        ? prev.filter(l => l !== length)
        : [...prev, length]
    );
  };

  // Validate form
  const isValid = () => {
    // Name, description, and photo are required
    if (!title.trim() || !description.trim() || photos.length === 0) return false;
    
    // Quantity must be positive
    if (parseInt(quantity) <= 0) return false;
    
    // If listing on marketplace, category and price are required
    if (listedOnMarketplace) {
      if (!marketplaceCategory) return false;
      if (!price) return false;
    }
    
    return true;
  };

  // Save creation
  const handleSave = async () => {
    if (!isValid()) {
      if (!title.trim()) {
        alert('Please enter a name for your creation');
      } else if (!description.trim()) {
        alert('Please enter a description for your creation');
      } else if (photos.length === 0) {
        alert('Please add at least one photo');
      } else if (listedOnMarketplace && !marketplaceCategory) {
        alert('Please select a category (Press-Ons, Nail Polish Jewelry, or Other) to list on the marketplace');
      } else if (listedOnMarketplace && !price) {
        alert('Please set a price to list on the marketplace');
      } else {
        alert('Please fill in all required fields');
      }
      return;
    }

    const user = auth.currentUser;
    if (!user) return;

    setSaving(true);
    try {
      // Utility function to remove undefined values from objects
      const removeUndefined = (obj: any): any => {
        if (Array.isArray(obj)) {
          return obj.map(item => removeUndefined(item));
        }
        if (obj && typeof obj === 'object' && obj.constructor === Object) {
          const cleaned: any = {};
          for (const [key, value] of Object.entries(obj)) {
            if (value !== undefined) {
              cleaned[key] = removeUndefined(value);
            }
          }
          return cleaned;
        }
        return obj;
      };

      // Build press-on details if applicable (only include defined values)
      let pressOnDetails: PressOnDetails | undefined;
      if (marketplaceCategory === 'pressons') {
        const details: Partial<PressOnDetails> = {};
        if (shapesAvailable.length > 0) details.shapesAvailable = shapesAvailable;
        if (lengthsAvailable.length > 0) details.lengthsAvailable = lengthsAvailable;
        if (customSizing) details.customSizing = true;
        if (sizingKitAvailable) details.sizingKitAvailable = true;
        if (guideUrl.trim()) details.guideUrl = guideUrl.trim();
        
        // Only set pressOnDetails if there are any values
        if (Object.keys(details).length > 0) {
          pressOnDetails = details as PressOnDetails;
        }
      }
      
      // Clean itemsUsed to remove any undefined photoUrl values
      const cleanedItemsUsed = itemsUsed.map(item => {
        const cleaned: any = {
          itemId: item.itemId,
          itemType: item.itemType,
          name: item.name,
          brand: item.brand || ''
        };
        if (item.photoUrl) {
          cleaned.photoUrl = item.photoUrl;
        }
        return cleaned;
      });

      // Build creation object (without timestamps - add those after cleaning)
      const creationData: any = {
        userId: user.uid,
        title: title.trim(),
        description: description.trim(),
        quantity: parseInt(quantity),
        photos,
        itemsUsed: cleanedItemsUsed,
        // Denormalized array-of-strings mirror of `itemsUsed` so the
        // "Manis featuring this" detail-page section can query with
        // `array-contains`. Recomputed on every write so the two fields
        // stay in sync.
        itemsUsedRefs: computeItemsUsedRefs(cleanedItemsUsed),
        creationType,
        listedOnMarketplace
      };

      // Only add optional fields if they have values (Firestore doesn't accept undefined)
      if (dateCreated) creationData.dateCreated = dateCreated;
      if (techniques.length > 0) creationData.techniques = techniques;
      if (difficulty) creationData.difficulty = difficulty as any;
      if (timeToComplete) creationData.timeToComplete = timeToComplete;
      
      if (listedOnMarketplace) {
        if (marketplaceCategory) creationData.marketplaceCategory = marketplaceCategory as any;
        if (price) creationData.price = parseFloat(price);
        creationData.openToTrades = openToTrades;
        creationData.marketplaceStatus = 'available';
      }
      
      if (pressOnDetails) {
        creationData.pressOnDetails = pressOnDetails;
      }

      // Clean out any remaining undefined values, then add timestamps
      const cleanedData = removeUndefined(creationData);
      cleanedData.createdAt = serverTimestamp();
      cleanedData.updatedAt = serverTimestamp();

      // Save to Firestore
      const creationsRef = collection(db, 'users', user.uid, 'creations');
      await addDoc(creationsRef, cleanedData);

      // Update usage counts for items
      const currentItemIds = new Set(itemsUsed.map(item => item.itemId));
      
      if (!editingCreation) {
        // NEW CREATION: Increment usage on all items
        for (const item of itemsUsed) {
          try {
            const itemRef = doc(db, 'users', user.uid, 'items', item.itemId);
            await updateDoc(itemRef, {
              timesUsed: increment(1),
              lastUsedDate: new Date().toISOString().split('T')[0]
            });
          } catch (error) {
            console.error(`Error updating usage for ${item.name}:`, error);
          }
        }
      } else {
        // EDITING: Only update items that changed
        
        // Increment usage for NEWLY ADDED items (in current but not in original)
        for (const item of itemsUsed) {
          if (!originalItemIds.has(item.itemId)) {
            try {
              const itemRef = doc(db, 'users', user.uid, 'items', item.itemId);
              await updateDoc(itemRef, {
                timesUsed: increment(1),
                lastUsedDate: new Date().toISOString().split('T')[0]
              });
            } catch (error) {
              console.error(`Error incrementing usage for ${item.name}:`, error);
            }
          }
        }
        
        // Decrement usage for REMOVED items (in original but not in current)
        for (const itemId of originalItemIds) {
          if (!currentItemIds.has(itemId)) {
            try {
              const itemRef = doc(db, 'users', user.uid, 'items', itemId);
              await updateDoc(itemRef, {
                timesUsed: increment(-1)
              });
            } catch (error) {
              console.error(`Error decrementing usage for removed item:`, error);
            }
          }
        }
      }

      onSave();
    } catch (error) {
      console.error('Error saving creation:', error);
      alert('Error saving creation. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Merienda, cursive' }}>
            {editingCreation ? 'Edit Creation' : 'New Creation'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Photos */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Photos <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-3">
              {photos.map((photo, idx) => (
                <div key={idx} className="relative w-24 h-24 rounded-lg overflow-hidden group">
                  <img src={photo.url} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => setDefaultPhoto(idx)}
                      className={`p-1.5 rounded-full ${photo.isDefault ? 'bg-green-500' : 'bg-white/80 hover:bg-white'}`}
                      title="Set as default"
                    >
                      <Check className={`w-4 h-4 ${photo.isDefault ? 'text-white' : 'text-gray-700'}`} />
                    </button>
                    <button
                      onClick={() => removePhoto(idx)}
                      className="p-1.5 bg-red-500 rounded-full hover:bg-red-600"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4 text-white" />
                    </button>
                  </div>
                  {photo.isDefault && (
                    <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-green-500 text-white text-xs rounded">
                      Default
                    </div>
                  )}
                </div>
              ))}
              <label className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-fuchsia-500 hover:bg-fuchsia-50 transition-colors">
                <Upload className="w-6 h-6 text-gray-400" />
                <span className="text-xs text-gray-400 mt-1">Add Photo</span>
                <input
                  type="file"
                  accept={ACCEPTED_IMAGE_TYPES}
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Creation Type */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {CREATION_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setCreationType(type.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    creationType === type.value
                      ? 'bg-fuchsia-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Galaxy Holo Mani, Rose Gold Press-On Set"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
              style={{ fontFamily: 'Nunito, sans-serif' }}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your creation..."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fuchsia-500 resize-none"
              style={{ fontFamily: 'Nunito, sans-serif' }}
            />
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Quantity <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
              style={{ fontFamily: 'Nunito, sans-serif' }}
            />
          </div>

          {/* Items Used */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Items Used
            </label>
            <div className="space-y-2">
              {itemsUsed.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {itemsUsed.map((item) => (
                    <div
                      key={item.itemId}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg group"
                    >
                      {item.photoUrl ? (
                        <img src={item.photoUrl} alt="" className="w-8 h-8 rounded object-cover" />
                      ) : (
                        <div className="w-8 h-8 bg-gray-300 rounded flex items-center justify-center">
                          <Image className="w-4 h-4 text-gray-500" />
                        </div>
                      )}
                      <div className="text-sm">
                        <p className="font-medium text-gray-800">{item.name}</p>
                        <p className="text-xs text-gray-500">{item.brand}</p>
                      </div>
                      <button
                        onClick={() => removeItemUsed(item.itemId)}
                        className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">No items added yet</p>
              )}
              <button
                onClick={() => setShowItemPicker(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                style={{ fontFamily: 'Nunito, sans-serif' }}
              >
                <Plus className="w-4 h-4" />
                Add from Collection
              </button>
              <p className="text-xs text-gray-500">
                💡 Usage counts will automatically increment when you save
              </p>
            </div>
          </div>

          {/* Optional Fields Toggle */}
          <button
            onClick={() => setShowOptional(!showOptional)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            {showOptional ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            <span className="font-medium" style={{ fontFamily: 'Nunito, sans-serif' }}>Optional Details</span>
          </button>

          {showOptional && (
            <div className="space-y-4 pl-4 border-l-2 border-gray-200">
              {/* Date Created */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Date Created</label>
                <input
                  type="date"
                  value={dateCreated}
                  onChange={(e) => setDateCreated(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
                />
              </div>

              {/* Techniques */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Techniques Used</label>
                <div className="flex flex-wrap gap-2">
                  {TECHNIQUES.map((tech) => (
                    <button
                      key={tech}
                      onClick={() => toggleTechnique(tech)}
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${
                        techniques.includes(tech)
                          ? 'bg-fuchsia-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {tech}
                    </button>
                  ))}
                </div>
              </div>

              {/* Difficulty */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Difficulty</label>
                <div className="flex gap-2">
                  {['beginner', 'intermediate', 'advanced'].map((level) => (
                    <button
                      key={level}
                      onClick={() => setDifficulty(difficulty === level ? '' : level)}
                      className={`px-4 py-2 rounded-lg capitalize transition-colors ${
                        difficulty === level
                          ? 'bg-fuchsia-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>

              {/* Time to Complete */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Time to Complete</label>
                <input
                  type="text"
                  value={timeToComplete}
                  onChange={(e) => setTimeToComplete(e.target.value)}
                  placeholder="e.g., 30 minutes, 2 hours"
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
                />
              </div>
            </div>
          )}

          {/* Marketplace Toggle */}
          <div className="border-t border-gray-200 pt-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={listedOnMarketplace}
                onChange={(e) => {
                  setListedOnMarketplace(e.target.checked);
                  setShowMarketplace(e.target.checked);
                }}
                className="w-5 h-5 rounded text-fuchsia-500 focus:ring-fuchsia-500"
              />
              <Store className="w-5 h-5 text-green-500" />
              <span className="font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
                List on Marketplace
              </span>
            </label>
          </div>

          {showMarketplace && listedOnMarketplace && (
            <div className="space-y-4 pl-4 border-l-2 border-green-200">
              {/* Category */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {MARKETPLACE_CATEGORIES.map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => {
                        setMarketplaceCategory(cat.value);
                        setShowPressOnDetails(cat.value === 'pressons');
                      }}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                        marketplaceCategory === cat.value
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      
                      <span>{cat.label}</span>
                    </button>
                  ))}
                </div>
                {!marketplaceCategory && (
                  <p className="text-xs text-amber-600 mt-1">Please select a category to list on marketplace</p>
                )}
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Price <span className="text-red-500">*</span>
                </label>
                <div className="relative w-32">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
                  />
                </div>
                {!price && (
                  <p className="text-xs text-amber-600 mt-1">Please enter a price</p>
                )}
              </div>

              {/* Open to Trades */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={openToTrades}
                    onChange={(e) => setOpenToTrades(e.target.checked)}
                    className="w-5 h-5 rounded text-blue-500 focus:ring-blue-500"
                  />
                  <span className="font-medium text-gray-700" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    🔄 Open to Trades
                  </span>
                  <span className="text-xs text-gray-400">(optional)</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-8">Allow others to propose trades for this item</p>
              </div>

              {/* Press-On Details */}
              {marketplaceCategory === 'pressons' && (
                <div className="space-y-4 p-4 bg-purple-50 rounded-lg">
                  <h4 className="font-bold text-purple-700" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    Press-On Details
                  </h4>

                  {/* Shapes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Shapes Available</label>
                    <div className="flex flex-wrap gap-2">
                      {NAIL_SHAPES.map((shape) => (
                        <button
                          key={shape}
                          onClick={() => toggleShape(shape)}
                          className={`px-3 py-1 rounded-full text-sm transition-colors ${
                            shapesAvailable.includes(shape)
                              ? 'bg-purple-500 text-white'
                              : 'bg-white text-gray-600 hover:bg-purple-100'
                          }`}
                        >
                          {shape}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Lengths */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Lengths Available</label>
                    <div className="flex flex-wrap gap-2">
                      {NAIL_LENGTHS.map((length) => (
                        <button
                          key={length}
                          onClick={() => toggleLength(length)}
                          className={`px-3 py-1 rounded-full text-sm transition-colors ${
                            lengthsAvailable.includes(length)
                              ? 'bg-purple-500 text-white'
                              : 'bg-white text-gray-600 hover:bg-purple-100'
                          }`}
                        >
                          {length}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Options */}
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={customSizing}
                        onChange={(e) => setCustomSizing(e.target.checked)}
                        className="w-4 h-4 rounded text-purple-500 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-600">Custom sizing available</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sizingKitAvailable}
                        onChange={(e) => setSizingKitAvailable(e.target.checked)}
                        className="w-4 h-4 rounded text-purple-500 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-600">Sizing kit available</span>
                    </label>
                  </div>

                  {/* Guide URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sizing Guide URL (optional)
                    </label>
                    <input
                      type="url"
                      value={guideUrl}
                      onChange={(e) => setGuideUrl(e.target.value)}
                      placeholder="https://yoursite.com/sizing-guide"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !isValid()}
            className="px-6 py-2 bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white rounded-lg font-bold hover:from-fuchsia-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            {saving ? 'Saving...' : 'Save Creation'}
          </button>
        </div>
      </div>

      {/* Item Picker Modal */}
      {showItemPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-bold" style={{ fontFamily: 'Merienda, cursive' }}>
                Add Items from Collection
              </h3>
              <button onClick={() => setShowItemPicker(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search & Filter */}
            <div className="p-4 border-b border-gray-100 flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search items..."
                  value={itemSearchQuery}
                  onChange={(e) => setItemSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
                />
              </div>
              <select
                value={itemTypeFilter}
                onChange={(e) => setItemTypeFilter(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
              >
                <option value="all">All Types</option>
                <option value="polish">Polish</option>
                <option value="plate">Plates</option>
                <option value="transfer">Transfers</option>
                <option value="embellishment">Embellishments</option>
                <option value="tool">Tools</option>
                <option value="powder">Powders</option>
                <option value="acrylic">Acrylics</option>
                <option value="cuticle_care">Cuticle Care</option>
              </select>
            </div>

            {/* Items List */}
            <div className="flex-1 overflow-y-auto p-4">
              {loadingItems ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-4 border-fuchsia-200 border-t-fuchsia-500 rounded-full animate-spin mx-auto" />
                </div>
              ) : filteredItems.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No items found</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {filteredItems.slice(0, 30).map((item) => {
                    const photo = item.photos?.find(p => p.isDefault) || item.photos?.[0];
                    return (
                      <button
                        key={item.id}
                        onClick={() => addItemUsed(item)}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-fuchsia-50 hover:ring-2 hover:ring-fuchsia-300 transition-all text-left"
                      >
                        {photo ? (
                          <img src={photo.url} alt="" className="w-12 h-12 rounded object-cover" />
                        ) : (
                          <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                            <span className="text-xl">💅</span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-800 truncate">{item.name}</p>
                          <p className="text-xs text-gray-500 truncate">{item.brand}</p>
                        </div>
                        <Plus className="w-5 h-5 text-fuchsia-500" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Done button */}
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => setShowItemPicker(false)}
                className="w-full py-2 bg-fuchsia-500 text-white rounded-lg font-bold hover:bg-fuchsia-600 transition-colors"
              >
                Done ({itemsUsed.length} items selected)
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Storage Upgrade Modal */}
      {showStorageUpgradeModal && (
        <StorageUpgradeModal
          isOpen={showStorageUpgradeModal}
          onClose={() => setShowStorageUpgradeModal(false)}
        />
      )}
    </div>
  );
};

export default AddCreationModal;
